<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, toRefs, watch, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { DropdownChangeEvent } from 'primevue/dropdown';
import { useDateTime, useRouting, can } from '@/composables';
import { PersonStores, useProgressReportStore, useReportStore } from '@/store';
import ReportSubjectTitle from '@/views/Reports/components/ReportSubjectTitle.vue';
import ProgressReportQuantity from '@/views/Reports/ProgressReport/ProgressReportQuantity.vue';
import ProgressReportIntensity from '@/views/Reports/ProgressReport/ProgressReportIntensity.vue';
import ProgressReportIntensityType from '@/views/Reports/ProgressReport/ProgressReportIntensityType.vue';
import { PERMISSIONS, EMIT } from '@/enum';
import { ProgressReportInterface } from '@/types';

interface Props {
  studentId?: undefined | string;
}

const props = withDefaults(defineProps<Props>(), { studentId: undefined });

const { studentId } = toRefs(props);

const $routing = useRouting();

const expandedRows = ref([]);
const reportHtml = ref('');
const showIframe = ref(false);

const emit = defineEmits([EMIT.QUERY_PARAMS]);

const profileStore = PersonStores.useUserProfileStore();
const reportStore = useReportStore();
const progressReportStore = useProgressReportStore();
const {
  filters: {
    value: { attendees },
  },
  attendeeName,
} = storeToRefs(progressReportStore);

const includeCaseloadManagement = ref(false);

const summary = ref('');

onMounted(() => init());

onUnmounted(() => progressReportStore.$reset());

$routing.routeChangeWatcher(() => {
  if (isProgressReport.value || isUserProgressReport.value) {
    progressReportStore.queryParams = $routing.route.query;
    defineFilters();
    doProgressReport();
  }
});

watch(
  () => [
    reportStore.filters.clinicians.selected,
    reportStore.filters.dates,
    reportStore.filters.organizations.selected,
  ],
  () => progressReportStore.getAttendees(),
);

// Disable the ability to download if no summary has been entered or
// a download is currently in progress.
const isDownloadDisabled = computed<boolean>(() => summary.value === '' || progressReportStore.progress.downloading);

const hasAttendeeSelected = computed<boolean>(() => !!attendees.selected || !!+studentId.value);

// TODO: If this report is necessary in additional areas of the application then
// this section could use some changes in order for adjustments.
const isProgressReport = computed<boolean>(() => $routing.route.name === 'reports.progress');
const isUserProgressReport = computed<boolean>(() => $routing.route.name === 'user.progress');

const progressReportData = computed<ProgressReportInterface[]>(() => progressReportStore.progress.data);

const reportAttendeeName = computed(() =>
  isUserProgressReport.value ? profileStore.person?.display_name : attendeeName.value,
);

const reportDates = computed(
  () =>
    `${useDateTime().formatDateShortMonth(reportStore.filters.dates[0])} - ${useDateTime().formatDateShortMonth(
      reportStore.filters.dates[1],
    )}`,
);

const init = async () => {
  // The reports section of the progress report requires the following
  // before it can go about doing anything.
  if (isProgressReport.value) {
    if (can([PERMISSIONS.REPORTS_FILTER_SERVICE_PROVIDERS])) await progressReportStore.getAttendees();
    await defineFilters();
    await doProgressReport();
  }

  // The user section of the progress report only requires date query
  // params to be loaded initially.
  if (isUserProgressReport.value) {
    reportStore.defineDefaultDates();
    doProgressReport();
  }
};

const doProgressReport = async () => {
  const params = $routing.route.query;
  const hasDateParams = params.start_at && params.end_at;
  const hasRequiredParams = hasDateParams && hasAttendeeSelected.value;

  // Make sure that all required query params exist before firing the request.
  if (hasRequiredParams) {
    // Only force add the attendee param for the user profile route.
    if (isUserProgressReport.value) params.attendee = studentId.value;
    await progressReportStore.getProgressReport($routing.route.query);
  }
};

const defineFilters = async () => {
  const query = $routing.route.query;
  attendees.selected = parseInt(query.attendee) || 0;
  includeCaseloadManagement.value = !!+query.cm;

  // Add back the query params for the report once it regains focus again.
  if (progressReportStore.queryParams) {
    $routing.addQueryParams(progressReportStore.queryParams);
  } else {
    emit(EMIT.QUERY_PARAMS);
  }
};

const defineLocation = (location) => {
  return location
    ? [location.address, location.city?.name, location.state?.code, location.zipcode]
        .filter((item) => !!item)
        .join(', ')
    : 'N/A';
};

const defineOrganizations = (organizations) => {
  return organizations?.length ? organizations.map((item) => item.name).join(', ') : 'N/A';
};

const defineAge = (birthday) => (birthday?.length ? useDateTime().getAge(birthday) : 'N/A');

const defineBirthDate = (birthday) => (birthday?.length ? useDateTime().formatToMMDDYYYY(birthday) : 'N/A');

const defineClinicians = (clinicians) => {
  return clinicians?.length ? clinicians.map((item) => item.full_name).join(', ') : 'N/A';
};

const definePhone = (location) => (location?.phone?.length ? location.phone : 'N/A');

const defineMeasurementPercentage = (measurement) => `${measurement ? Math.round(measurement) : 0}%`;

const onAttendeeChange = (event: DropdownChangeEvent) => {
  summary.value = '';

  if (event.value) {
    $routing.addQueryParam('attendee', event.value);
  } else {
    $routing.removeQueryParam('attendee');
  }
};

const onDownloadReport = async () => {
  const data = progressReportData.value;
  data.age = useDateTime().getAge(data.birthday);

  const payload = {
    summary: summary.value,
    data: progressReportData.value,
    start_at: useDateTime().formatDateForApi(reportStore.filters.dates[0]),
    end_at: useDateTime().formatDateForApi(reportStore.filters.dates[1]),
  };

  const response = await progressReportStore.downloadProgressReport(payload);
  reportHtml.value = response;
  showIframe.value = true;

  nextTick(() => {
    const printFrame = document.getElementById('progress-report-frame');
    if (printFrame) {
      const frameWindow = printFrame.contentWindow;
      if (frameWindow) {
        frameWindow.onafterprint = () => {
          showIframe.value = false;
        };
        setTimeout(() => {
          frameWindow.print();
        }, 500);
      }
    }
  });
};

const onIncludeCaseloadManagement = () => $routing.addQueryParam('cm', includeCaseloadManagement.value ? '1' : '0');
</script>

<template>
  <div class="page-content">
    <iframe v-if="showIframe" id="progress-report-frame" :srcdoc="reportHtml" class="print-iframe" />
    <Card>
      <template #content>
        <div class="grid pt-3">
          <div class="col-12 md:col-6 lg:col-2">
            <Dropdown
              v-if="!studentId && can([PERMISSIONS.REPORTS_FILTER_SERVICE_PROVIDERS])"
              id="reports_progress_attendee"
              v-model="attendees.selected"
              :options="attendees.options"
              :loading="attendees.loading"
              :filter="true"
              :reset-filter-on-hide="true"
              class="w-full"
              option-label="name"
              option-value="value"
              placeholder="Attendee"
              @change="onAttendeeChange"
            />
            <AppDateRangePicker
              v-else
              v-model="reportStore.filters.dates"
              :value="reportStore.filters.dates"
              @change="reportStore.onDateRangeChange"
            />
          </div>
          <div class="col-fixed md:col lg:col">
            <div class="mr-2 pt-1" style="float: left">
              <InputSwitch v-model="includeCaseloadManagement" @input="onIncludeCaseloadManagement" />
            </div>
            <div class="pt-2 text-gray-600" style="float: left">
              <label>Include Caseload Management</label>
            </div>
          </div>
          <div class="col-fixed">
            <AppButton
              type="secondary"
              text="Download"
              icon-left="system_update_alt"
              :disabled="isDownloadDisabled"
              @click="onDownloadReport"
            />
          </div>
        </div>
        <template v-if="!hasAttendeeSelected">
          <div class="grid h-20rem mt-1 p-2">
            <div class="flex-auto flex align-items-center justify-content-center surface-100 border-round">
              <span class="text-800 text-xs">Select an Attendee to see Progress Report</span>
            </div>
          </div>
        </template>
        <template v-else>
          <ReportSubjectTitle title="Progress" :name="reportAttendeeName" :dates="reportStore.filters.dates" />
          <div class="grid pr-3 pl-3">
            <div class="col mt-2 p-0 w-full">
              <div class="field w-full">
                <label for="username" class="required">Summary</label>
                <InputText
                  id="summary"
                  v-model="summary"
                  type="text"
                  class="w-full"
                  placeholder="Enter progress report summary here"
                />
              </div>
            </div>
          </div>
          <div class="grid p-3">
            <div class="col p-5 border-round border-1 border-200 text-center">
              <div class="grid">
                <div class="col text-left">
                  <img alt="eLuma logo" height="36" src="@/assets/eluma-logo.svg" />
                  <img class="mb-2 ml-2" alt="eLuma text" height="17" src="@/assets/eluma-text.svg" />
                </div>
                <div class="col text-right">
                  <p class="text-xl font-semibold text-600">Progress Report</p>
                  <p v-text="reportDates"></p>
                </div>
              </div>
              <div class="grid pb-5">
                <div class="col text-center">
                  <h2 class="pt-2 pb-2 text-600">Student</h2>
                  <div class="grid">
                    <div class="col">
                      <div class="grid">
                        <div class="col text-right text-600">SSID</div>
                        <div class="col text-left font-semibold">
                          {{
                            progressReportData.external_association
                              ? progressReportData.external_association.external_id
                              : 'N/A'
                          }}
                        </div>
                      </div>
                      <div class="grid">
                        <div class="col text-right text-600">Name</div>
                        <div class="col text-left font-semibold">
                          <span v-text="progressReportData.full_name" />
                        </div>
                      </div>
                      <div class="grid">
                        <div class="col text-right text-600">Address</div>
                        <div class="col text-left font-semibold">
                          <span v-text="defineLocation(progressReportData.location)" />
                        </div>
                      </div>
                      <div class="grid">
                        <div class="col text-right text-600">Phone</div>
                        <div class="col text-left font-semibold">
                          <span v-text="definePhone(progressReportData.location)" />
                        </div>
                      </div>
                      <div class="grid">
                        <div class="col text-right text-600">Date of Birth</div>
                        <div class="col text-left font-semibold">
                          <span v-text="defineBirthDate(progressReportData.birthday)" />
                        </div>
                      </div>
                      <div class="grid">
                        <div class="col text-right text-600">Age</div>
                        <div class="col text-left font-semibold">
                          <span v-text="defineAge(progressReportData.birthday)" />
                        </div>
                      </div>
                    </div>
                    <div class="col">
                      <div class="grid">
                        <div class="col text-right text-600">Customer</div>
                        <div class="col text-left font-semibold">
                          <span v-text="defineOrganizations(progressReportData.organizations)" />
                        </div>
                      </div>
                      <div class="grid">
                        <div class="col text-right text-600">Service Providers</div>
                        <div class="col text-left font-semibold">
                          <span v-text="defineClinicians(progressReportData.clinicians)" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="grid pb-5">
                <div class="col text-center">
                  <h2 class="pt-2 pb-2 text-600">Services</h2>
                  <div class="grid">
                    <div class="col">
                      <DataTable
                        v-model:expandedRows="expandedRows"
                        :value="progressReportData.treatment_services"
                        responsive-layout="scroll"
                      >
                        <template #empty>No services found</template>
                        <Column field="" :expander="true" header-class="no-center" header-style="width: 20px" />
                        <Column field="code" header="Service">
                          <template #body="{ data }">
                            <p class="text-base font-semibold" v-text="data.service.name" />
                            <p v-text="data.discipline.name" />
                          </template>
                        </Column>
                        <Column field="duration" header="Duration">
                          <template #body="{ data }">
                            <span class="ml-4" v-text="data.duration" />
                          </template>
                        </Column>
                        <Column field="start_at" header="Start Date">
                          <template #body="{ data }">
                            <span v-text="useDateTime().formatIntlDate(data.start_at)" />
                          </template>
                        </Column>
                        <Column field="end_at" header="End Date">
                          <template #body="{ data }">
                            <span v-text="useDateTime().formatIntlDate(data.end_at)" />
                          </template>
                        </Column>
                        <template #expansion="slotProps">
                          <div class="col text-left">
                            <h2 class="pb-2 text-600">Goals</h2>
                            <div class="grid">
                              <div class="col">
                                <DataTable :value="slotProps.data.goals" responsive-layout="scroll">
                                  <template #empty>No goals found</template>
                                  <Column field="treatment_goal_type.title" header="Type" />
                                  <Column field="description" header="Description" />
                                  <Column field="measurements_avg_percentage" header="Percentage">
                                    <template #body="{ data }">
                                      <span
                                        class="ml-4"
                                        v-text="defineMeasurementPercentage(data.measurements_avg_percentage)"
                                      />
                                    </template>
                                  </Column>
                                  <Column field="data" header="Quantity">
                                    <template #body="{ data }">
                                      <ProgressReportQuantity :goal-id="data.id" />
                                    </template>
                                  </Column>
                                  <Column field="data" header="Intensity">
                                    <template #body="{ data }">
                                      <ProgressReportIntensity :goal-id="data.id" />
                                    </template>
                                  </Column>
                                  <Column field="data" header="Intensity Type">
                                    <template #body="{ data }">
                                      <ProgressReportIntensityType :goal-id="data.id" />
                                    </template>
                                  </Column>
                                </DataTable>
                              </div>
                            </div>
                          </div>
                        </template>
                      </DataTable>
                    </div>
                  </div>
                </div>
              </div>
              <div class="grid">
                <div class="col text-center">
                  <h2 class="pt-2 pb-2 text-600">Events</h2>
                  <div class="grid">
                    <div class="col">
                      <DataTable :value="progressReportData.events" responsive-layout="scroll">
                        <template #empty>No events found</template>
                        <Column field="start_at" header="Date">
                          <template #body="{ data }">
                            <span v-text="data.date" />
                          </template>
                        </Column>
                        <Column field="start_at" header="Time">
                          <template #body="{ data }">
                            <span v-text="data.time" />
                          </template>
                        </Column>
                        <Column field="event_type" header="Event Type" />
                        <Column field="service_provider" header="Service Provider" />
                        <Column field="results" header="Attn Type">
                          <template #body="{ data }">
                            <span v-text="data.therapy_result_type" />
                          </template>
                        </Column>
                        <Column field="results" header="Duration">
                          <template #body="{ data }">
                            <span class="ml-4" v-text="data.duration" />
                          </template>
                        </Column>
                        <Column field="results" header="Notes">
                          <template #body="{ data }">
                            <span v-text="data.notes" />
                          </template>
                        </Column>
                      </DataTable>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </template>
    </Card>
  </div>
</template>

<style lang="scss" scoped>
.print-iframe {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  border: none;
  background: white;
}

@media print {
  .print-iframe {
    position: static;
    width: 100%;
    height: auto;
  }
}

:deep(.p-card-body) {
  .border-round {
    border-radius: 6px !important;
  }

  .p-datatable-table {
    border: 1px solid #f1f3f3;
  }
}
</style>
